import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Box,
  Typography,
  Radio,
  RadioGroup,
  Switch,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { FieldRendererProps } from './types';
import { ImageComponent } from './ImageComponent';
import { hasApiConfig, getApiConfig, useApiData } from './apiUtils';

export const FieldRenderer: React.FC<FieldRendererProps> = ({ field, control, errors, baseUrl, apiHeaders, formData }) => {
  const error = errors[field.name];
  const hasError = !!error;
  const [hasInteracted, setHasInteracted] = useState(false);
  const hasInteractedRef = useRef(false);

  // Helper function to determine if field is required
  const isFieldRequired = () => {
    // Check multiple possible locations for required flag
    if (field.required !== undefined) {
      return field.required;
    }
    if (field.validation?.required !== undefined) {
      return field.validation.required;
    }
    // Default to false if not specified
    return false;
  };

  // Helper function to get label with asterisk for required fields
  const getLabelWithAsterisk = (label: string) => {
    return isFieldRequired() ? `${label} *` : label;
  };

  // Helper function to get default value
  const getDefaultValue = () => {
    if (field.value !== undefined) {
      return field.value;
    }
    if (field.type === 'checkbox') {
      return false;
    }
    if (field.type === 'select' && (field as any).default) {
      return (field as any).default;
    }
    return '';
  };

  // Memoize the API config to prevent unnecessary re-renders
  const apiConfig = useMemo(() => {
    return getApiConfig(field);
  }, [field.apiConfig, field.api_endpoint, field.api_method, field.value_field, field.label_field, field.depends_on, field.conditional]);

  // Use ref to track the previous dependent value to avoid unnecessary re-renders
  const previousDependentValueRef = useRef<any>(null);
  
  // Get the current dependent value
  const dependentValue = apiConfig?.dependsOn ? formData?.[apiConfig.dependsOn] : null;
  
  // Check if dependent value has changed using ref to avoid re-renders
  const dependentValueChanged = useMemo(() => {
    if (!apiConfig?.dependsOn) return false;
    const currentValue = dependentValue;
    const previousValue = previousDependentValueRef.current;
    const hasChanged = currentValue !== previousValue;
    if (hasChanged) {
      previousDependentValueRef.current = currentValue;
    }
    return hasChanged;
  }, [apiConfig?.dependsOn, dependentValue]);

  // Memoize the should show field check - always show the field, handle disabled state separately
  const shouldShow = useMemo(() => {
    // Always show the field - disabled state will be handled in the field rendering
    return true;
  }, []);

  // Memoize the enabled condition to prevent unnecessary re-renders
  const isApiEnabled = useMemo(() => {
    return hasInteracted && hasApiConfig(field) && (!apiConfig?.dependsOn || dependentValue);
  }, [hasInteracted, field, apiConfig?.dependsOn, dependentValue]);

  // Memoize the dependent values to prevent unnecessary re-renders
  const dependentValues = useMemo(() => {
    if (!apiConfig?.dependsOn || !formData) return {};
    
    const dependentData: Record<string, any> = {};
    if (apiConfig.dependsOn && formData[apiConfig.dependsOn] !== undefined) {
      dependentData[apiConfig.dependsOn] = formData[apiConfig.dependsOn];
    }
    return dependentData;
  }, [apiConfig?.dependsOn, formData]);

  // Use React Query for API calls - only enabled when user interacts
  const {
    data: dynamicOptions = [],
    isLoading,
    error: apiError
  } = useApiData(
    apiConfig,
    baseUrl,
    apiHeaders,
    dependentValues, // Only pass dependent values, not entire formData
    isApiEnabled
  );

  // Handle select open specifically - only trigger once
  const handleSelectOpen = useCallback(() => {
    if (hasApiConfig(field) && !hasInteractedRef.current) {
      hasInteractedRef.current = true;
      setHasInteracted(true);
    }
  }, [field]);

  // Handle autocomplete open specifically - only trigger once
  const handleAutocompleteOpen = useCallback(() => {
    if (hasApiConfig(field) && !hasInteractedRef.current) {
      hasInteractedRef.current = true;
      setHasInteracted(true);
    }
  }, [field]);

  // Check if field has a value and should be considered as interacted
  useEffect(() => {
    if (hasApiConfig(field) && !hasInteractedRef.current) {
      const currentValue = formData?.[field.name];
      if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
        // If field has a value, consider it as interacted
        hasInteractedRef.current = true;
        setHasInteracted(true);
      }
    }
  }, [field, formData]);

  // Handle dependent field changes - reset fetch state if dependent value changes
  useEffect(() => {
    if (hasApiConfig(field) && apiConfig?.dependsOn && dependentValueChanged && hasInteractedRef.current) {
      // If dependent value changed, reset fetch state to refetch
      hasInteractedRef.current = false;
      setHasInteracted(false);
    }
  }, [dependentValueChanged, apiConfig?.dependsOn]);

  // Get options for the field (static or dynamic)
  const getOptions = useCallback((): Array<{ label: string; value: string | number }> => {
    if (hasApiConfig(field)) {
      return dynamicOptions;
    }
    // Type-safe way to get options
    if (field.type === 'select' || field.type === 'toggle' || field.type === 'radio' || field.type === 'autocomplete') {
      return (field as any).options || [];
    }
    return [];
  }, [field, dynamicOptions]);

  // Check if field should be shown
  if (!shouldShow) {
    return null;
  }

  const renderField = () => {
    switch (field.type) {
      case 'title':
        return (
          <Typography
            variant={field.variant || 'h4'}
            component="h1"
            color={field.color}
            align={field.align || 'left'}
            sx={{ mb: 3, fontWeight: 600, ...field.sx }}
          >
            {field.label}
          </Typography>
        );

      case 'section':
        return (
          <Typography
            variant={field.variant || 'h6'}
            component="h3"
            color={field.color}
            align={field.align || 'left'}
            sx={{ mb: 2, fontWeight: 600, color: 'text.primary', ...field.sx }}
          >
            {field.label}
          </Typography>
        );

      case 'text':
      case 'email':
      case 'password':
      case 'tel':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                id={field.id}
                fullWidth
                label={getLabelWithAsterisk(field.label)}
                type={field.type}
                placeholder={field.placeholder}
                disabled={field.disabled}
                error={hasError}
                helperText={error?.message}
              />
            )}
          />
        );

      case 'number':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                id={field.id}
                fullWidth
                label={getLabelWithAsterisk(field.label)}
                type="number"
                placeholder={field.placeholder}
                disabled={field.disabled}
                error={hasError}
                helperText={error?.message}
                inputProps={{
                  min: field.min,
                  max: field.max,
                }}
              />
            )}
          />
        );

      case 'select':
        const options = getOptions();
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <FormControl fullWidth error={hasError || !!apiError}>
                <InputLabel id={field.id}>{getLabelWithAsterisk(field.label)}</InputLabel>
                <Select
                  {...controllerField}
                  labelId={field.id}
                  label={getLabelWithAsterisk(field.label)}
                  disabled={field.disabled || isLoading}
                  endAdornment={isLoading ? <CircularProgress size={20} /> : undefined}
                  onOpen={handleSelectOpen}
                >
                  {isLoading ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Fetching data...</Typography>
                      </Box>
                    </MenuItem>
                  ) : options.length > 0 ? (
                    options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  ) : hasInteracted && !isLoading ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No options available
                      </Typography>
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        Click to load options
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
                {(hasError || apiError) && (
                  <FormHelperText>{error?.message || (apiError as Error)?.message}</FormHelperText>
                )}
                
              </FormControl>
            )}
          />
        );

      case 'checkbox':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <Box sx={{ mt: 1, mb: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...controllerField}
                      id={field.id}
                      checked={controllerField.value}
                      disabled={field.disabled}
                    />
                  }
                  label={getLabelWithAsterisk(field.label)}
                />
                {hasError && (
                  <FormHelperText error sx={{ ml: 0 }}>
                    {error?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        );

      case 'toggle':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <FormControl component="fieldset" error={hasError}>
                <Typography variant="body2" component="legend" sx={{ mb: 1 }}>
                  {getLabelWithAsterisk(field.label)}
                </Typography>
                <RadioGroup
                  {...controllerField}
                  row
                >
                  {field.options.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {hasError && (
                  <FormHelperText error>
                    {error?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        );

      case 'radio':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <FormControl component="fieldset" error={hasError}>
                <Typography variant="body2" component="legend" sx={{ mb: 1 }}>
                  {getLabelWithAsterisk(field.label)}
                </Typography>
                <RadioGroup
                  {...controllerField}
                >
                  {field.options.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {hasError && (
                  <FormHelperText error>
                    {error?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        );

      case 'switch':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <Box sx={{ mt: 1, mb: 0 }}>
                <FormControlLabel
                  control={
                    <Switch
                      {...controllerField}
                      id={field.id}
                      checked={controllerField.value}
                      disabled={field.disabled}
                    />
                  }
                  label={getLabelWithAsterisk(field.label)}
                />
                {hasError && (
                  <FormHelperText error sx={{ ml: 0 }}>
                    {error?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        );

      case 'autocomplete':
        const autocompleteOptions = getOptions();
        
        // Check if this field depends on another field and if that field has a value
        const isDisabled: boolean = !!(field.disabled || isLoading || (apiConfig?.dependsOn && !dependentValue));
        
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => {
              // Get the current value and find the corresponding option
              const currentValue = controllerField.value;
              const selectedOption = (() => {
                if (!currentValue) return null;
                
                const valueField = apiConfig?.valueField || field.value_field || 'value';
                const labelField = apiConfig?.labelField || field.label_field || 'label';
                
                // First try to find the option in the loaded options
                const foundOption = autocompleteOptions.find((option: any) => {
                  if (typeof option === 'string') {
                    return option === currentValue;
                  }
                  return (option as any)[valueField] === currentValue || (option as any).value === currentValue || (option as any).id === currentValue;
                });
                
                if (foundOption) {
                  return foundOption;
                }
                
                // If option not found but value exists, create a fallback option
                // This happens when navigating back to a step where options haven't been loaded yet
                if (currentValue) {
                  // Create a fallback option to display the current value
                  const fallbackOption = {
                    [valueField]: currentValue,
                    [labelField]: `Selected: ${currentValue}`,
                    value: currentValue,
                    label: `Selected: ${currentValue}`,
                  };
                  return fallbackOption;
                }
                
                return null;
              })();
              
              return (
                <Autocomplete
                  {...controllerField}
                  id={field.id}
                  options={autocompleteOptions}
                  value={selectedOption}
                  getOptionLabel={(option: any) => {
                    if (typeof option === 'string') return option;
                    // Use the configured label_field or fallback to common properties
                    const labelField = apiConfig?.labelField || field.label_field || 'label';
                    return (option as any)[labelField] || (option as any).label || (option as any).name || '';
                  }}
                  isOptionEqualToValue={(option: any, value: any) => {
                    if (!option || !value) return false;
                    
                    if (typeof option === 'string' && typeof value === 'string') {
                      return option === value;
                    }
                    
                    // Handle both object and primitive value comparisons
                    if (typeof option === 'object' && value) {
                      const valueField = apiConfig?.valueField || field.value_field || 'value';
                      if (typeof value === 'object') {
                        return (option as any)[valueField] === (value as any)[valueField] || (option as any).value === (value as any).value || (option as any).id === (value as any).id;
                      } else {
                        return (option as any)[valueField] === value || (option as any).value === value || (option as any).id === value;
                      }
                    }
                    return false;
                  }}
                  multiple={(field as any).multiple}
                  freeSolo={(field as any).freeSolo}
                  filterOptions={(field as any).filterOptions === false ? () => autocompleteOptions : undefined}
                  disabled={isDisabled}
                  loading={isLoading}
                  onOpen={handleAutocompleteOpen}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={getLabelWithAsterisk(field.label)}
                      placeholder={
                        isDisabled && apiConfig?.dependsOn && !dependentValue
                          ? `Please select ${apiConfig.dependsOn.replace(/_/g, ' ')} first`
                          : field.placeholder
                      }
                      error={hasError || !!apiError}
                      helperText={error?.message || (apiError as Error)?.message || ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  onChange={(_, newValue) => {
                    // Extract the value from the selected option based on field configuration
                    let valueToSet;
                    if (newValue && typeof newValue === 'object') {
                      // Use the configured value_field or fallback to common properties
                      const valueField = apiConfig?.valueField || field.value_field || 'value';
                      valueToSet = (newValue as any)[valueField] || (newValue as any).value || (newValue as any).id || newValue;
                    } else {
                      valueToSet = newValue;
                    }
                    controllerField.onChange(valueToSet);
                  }}
                  noOptionsText={
                    isDisabled && apiConfig?.dependsOn && !dependentValue
                      ? `Please select ${apiConfig.dependsOn.replace(/_/g, ' ')} first`
                      : isLoading 
                      ? "Fetching data..." 
                      : hasInteracted && !isLoading && autocompleteOptions.length === 0
                      ? "No options available"
                      : "Click to load options"
                  }
                />
              );
            }}
          />
        );

      case 'date':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                id={field.name}
                fullWidth
                label={getLabelWithAsterisk(field.label)}
                type="date"
                disabled={field.disabled}
                error={hasError}
                helperText={error?.message}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: field.minDate,
                  max: field.maxDate,
                }}
              />
            )}
          />
        );

      case 'image':
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={getDefaultValue()}
            render={({ field: controllerField }) => (
              <ImageComponent
                src={controllerField.value || field.defaultValue || field.src}
                alt={field.alt || field.label}
                width={field.width}
                height={field.height}
                style={field.style}
                className={field.className}
                onClick={field.onClick}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Render the actual field */}
      {renderField()}
    </Box>
  );
};